import { useResultBuffer } from '@/hooks/useResultBuffer';
import uniqueId from 'lodash/uniqueId';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { shuffle as shuffleArray } from '../../utils/shuffle';
import { ClickableWordSquareContainer } from '../ClickableWordSquare/container';
import { FeedBack } from '../FeedBack';
import { FullKeyboard } from '../FullKeyboard';
import { LetterSquareContainer } from '../LetterSquare/container';
import OfflineImage from '../SharedComponents/OfflineImage/OfflineImage';
import { WordSquareContainer } from '../WordSquare/container';
import { OfflineWrapper } from '@/layouts/OfflineWrapper';
import ResponsiveText from '@/components/SharedComponents/ResponsiveText/ResponsiveText';
import { VideoPlayer } from '../Video';
import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import { hasOwnProperty } from '@/utils/utils';
import { styles } from './styles';

//method to fix toUpperCase turning ß into SS
// eslint-disable-next-line no-extend-native
String.prototype.toFixedUppercase = function () {
  let upperCaseString = '';
  for (let ii = 0; ii < ('' + this).length; ii++) {
    if ('' + this[ii] !== 'ß') {
      upperCaseString = upperCaseString + '' + this[ii].toUpperCase();
    } else {
      upperCaseString = upperCaseString + '' + this[ii];
    }
  }
  return upperCaseString;
};

function findAllIndexes(str, letter) {
  const indexes = [];

  for (let i = 0; i < str.length; i++) {
    if (str[i] === letter) {
      indexes.push(i);
    }
  }
  return indexes;
}

function isWordFilled(arr) {
  return arr.every((eachElem) => eachElem !== null);
}

function isWordsFilled(arr, emptySpaces) {
  if (emptySpaces.length === 0) {
    return arr.every((eachElem) => eachElem !== null);
  }
  const filteredArr = arr.filter((eachElem, index) => {
    return !emptySpaces.includes(index);
  });
  return filteredArr.every((eachElem) => eachElem !== null);
}

let mistakes = {};

const generateExerciseType = (setting, name) => {
  return {
    ...setting,
    name,
  };
};

const recursiveFunction = (value, array, returnedArray) => {
  if (value === 0) {
    return returnedArray;
  }
  const randomWordIndex = Math.floor(Math.random() * array.length);
  if (returnedArray.includes(randomWordIndex)) {
    return recursiveFunction(value, array, returnedArray);
  } else {
    const newValue = value - 1;
    returnedArray.push(randomWordIndex);
    return recursiveFunction(newValue, array, returnedArray);
  }
};

const generateSetItemType = (key) => `${key}setitem`;

export function WritingExerciseSentence({
  isSolved,
  exercise,
  settings,
  peek,
  patientId,
  isSelf,
  next,
}) {
  const { setting, value, playbackRate } = settings;
  const { shuffle, word, optionalWrongWords } = exercise;
  const correctImage = exercise?.retinaImage ?? exercise?.correct?.[0];
  const audioUrl = exercise?.audio ?? '';

  const [wrongBoxChosen, setWrongBoxChosen] = useState(0);
  const [currentOrder, setCurrentOrder] = useState(0);
  const currentOrderAsKey = String(currentOrder);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [correctWord, setCorrectWord] = useState(
    new Array(word.length).fill(null),
  );
  const [guessedWords, setGuessedWords] = useState([]);
  const [correctWords, setCorrectWords] = useState(
    new Array(word.split(' ')?.length).fill(null),
  );

  const wordOrder = useMemo(() => {
    return correctWords.findIndex((eachValue) => eachValue === null);
  }, [correctWords]);
  const wordOrderAsKey = String(wordOrder);

  const isFullKeyboard = setting === 'full';
  const isGap = setting === 'gap';
  const isAnagram = setting === 'anagram';
  const setItemType = generateSetItemType(exercise.exerciseClass);

  const emptySpaces = useMemo(
    () => (isFullKeyboard ? findAllIndexes(word, ' ') : []),
    [word],
  );

  const { resultCreator } = useResultBuffer({
    setItemType,
    setItemTypeId: exercise.wordsetitem,
    exerciseType: generateExerciseType(settings, exercise?.type),
    patientId,
    shouldTracksMistakes: true,
    detailedPerformance: true,
    isSelf,
  });

  const capitalizeAll = useRef((word, currentOrder) => {
    const isUpper = word[currentOrder] !== word[currentOrder]?.toLowerCase();
    return isUpper;
  });

  const isCapital = capitalizeAll.current(word, currentOrder);

  const allCorrect = useMemo(() => {
    return isWordsFilled(correctWord, emptySpaces);
  }, [correctWord, emptySpaces]);
  const allWordsCorrect = useMemo(() => {
    return isWordFilled(correctWords);
  }, [correctWords]);
  const isCorrect = allWordsCorrect || allCorrect;

  const consecutiveWrongBoxChosen = useCallback((reset = false) => {
    if (reset) {
      setWrongBoxChosen(0);
    } else {
      setWrongBoxChosen((prev) => prev + 1);
    }
  }, []);

  const resetState = useCallback((word) => {
    // ? Info -> this function is responsible for reseting the state if the exercise is skipped or solved
    setCurrentOrder(0);
    setGuessedLetters([]);
    setCorrectWord(new Array(word?.length).fill(null));
    mistakes = {};

    if (isGap) {
      const wordSplit = word.split(' ');
      const noGaps = wordSplit.length <= value;
      if (noGaps) {
        setGuessedWords([]);
        setCorrectWords(new Array(word.split(' ')?.length).fill(null));
      } else {
        const defaultGuessedWords = [];
        const stateDefaultValue = new Array(word.split(' ')?.length).fill(null);
        const gapsNeeded = wordSplit.length - value;
        recursiveFunction(gapsNeeded, wordSplit, defaultGuessedWords);
        defaultGuessedWords.forEach((index) => {
          stateDefaultValue[index] = wordSplit[index];
        });
        setGuessedWords(defaultGuessedWords);
        setCorrectWords(stateDefaultValue);
      }
    } else {
      setGuessedWords([]);
      setCorrectWords(new Array(word.split(' ')?.length).fill(null));
    }
  }, []);

  const splitLetters = useMemo(() => {
    return shuffle.split('').map((letter, index) => {
      const includedIndexes = findAllIndexes(
        word.toLowerCase(),
        letter.toLowerCase(),
      );
      const order = includedIndexes.length === 0 ? -1 : includedIndexes;
      const returnedObj = {
        order,
        letter: !isCapital ? letter.toLowerCase() : letter.toFixedUppercase(),
        i: index,
        key: uniqueId(),
      };
      return returnedObj;
    });
  }, [shuffle, word, guessedLetters.length, isCapital]);

  const splitWords = useMemo(() => {
    if (isAnagram) {
      shuffleArray(optionalWrongWords);
      const randomWrongWords = [...optionalWrongWords]
        .splice(0, value)
        .map((eachOptionalWrongWord) => ({
          word: eachOptionalWrongWord,
          order: -1,
          key: uniqueId('wrong'),
        }));
      const correctWords = word.split(' ').map((eachWord, index) => {
        return {
          word: eachWord,
          order: index,
          key: uniqueId('correct'),
        };
      });
      shuffleArray(randomWrongWords);
      const mixedWords = correctWords.concat(randomWrongWords);
      shuffleArray(mixedWords);

      const mixedWordsAfterShuffle = mixedWords.map((mixedWord, index) => {
        return {
          ...mixedWord,
          i: index,
        };
      });
      return mixedWordsAfterShuffle;
    }
    if (isGap) {
      if (value === 1) {
        const renderedWords = word.split(' ').map((eachWord, index) => {
          return {
            word: eachWord,
            order: index,
            key: uniqueId('correct'),
            i: index,
          };
        });
        const randomWordIndex = Math.floor(
          Math.random() * optionalWrongWords.length,
        );
        const randomWord = optionalWrongWords[randomWordIndex];
        renderedWords.push({
          word: randomWord,
          order: -1,
          key: uniqueId('correct'),
          i: undefined,
        });
        shuffleArray(renderedWords);
        return renderedWords;
      } else {
        const renderedWords = word.split(' ').map((eachWord, index) => {
          return {
            word: eachWord,
            order: index,
            key: uniqueId('correct'),
            i: index,
          };
        });
        shuffleArray(renderedWords);
        return renderedWords;
      }
    }
    if (isFullKeyboard) {
      return [];
    }
  }, [word]);

  const onCorrect = useCallback((id, currentOrder, word) => {
    consecutiveWrongBoxChosen(true);
    setCorrectWords((prevState) => {
      const clonedState = [...prevState];
      clonedState[currentOrder] = word;
      return clonedState;
    });
    if (id === undefined) {
      return;
    }
    setTimeout(() => {
      setGuessedWords((prevState) => [...prevState, id]);
    }, 500);
  }, []);

  const onFullKeyboardCorrect = useCallback(
    (id, currentOrder, letter) => {
      consecutiveWrongBoxChosen(true);
      setCorrectWord((prevState) => {
        const clonedState = [...prevState];
        clonedState[currentOrder] = letter;
        return clonedState;
      });
      setCurrentOrder((prevState) => {
        const newState = prevState + 1;
        if (emptySpaces.includes(newState)) {
          return newState + 1;
        }
        return newState;
      });
      if (id === undefined) {
        return;
      }
      setTimeout(() => {
        setGuessedLetters((prevState) => [...prevState, id]);
      }, 500);
    },
    [emptySpaces.toString()],
  );

  const onWrong = useCallback(
    (i) => {
      consecutiveWrongBoxChosen(false);
      if (isFullKeyboard) {
        if (hasOwnProperty(mistakes, currentOrderAsKey)) {
          if (mistakes[currentOrderAsKey].includes(i)) {
            return;
          }
          mistakes[currentOrderAsKey].push(i);
        } else {
          mistakes[currentOrderAsKey] = [i];
        }
      } else {
        if (hasOwnProperty(mistakes, wordOrderAsKey)) {
          if (mistakes[wordOrderAsKey].includes(i)) {
            return;
          }
          mistakes[wordOrderAsKey].push(i);
        } else {
          mistakes[wordOrderAsKey] = [i];
        }
      }
    },
    [currentOrderAsKey, wordOrderAsKey],
  );

  const responsiveStyles = StyleSheet.create({
    imageContainer: {
      width: useResponsiveFontSize(17),
      aspectRatio: 1,
    },
  });

  useEffect(() => {
    resetState(word);
  }, [word]);

  useEffect(() => {
    if (!isCorrect) {
      return;
    }
    const mistakesNumbered = Object.values(mistakes).flat().length;
    if (!isSelf) {
      isSolved();
    }
    resultCreator(mistakesNumbered);
  }, [isCorrect]);

  return (
    <View style={styles.flex1}>
      {isFullKeyboard ? (
        <LetterSquareContainer
          emptySpaces={emptySpaces}
          correctWord={correctWord}
        />
      ) : (
        <WordSquareContainer word={word} correctWords={correctWords} />
      )}
      <View style={styles.sentenceExerciseArea}>
        <View style={responsiveStyles.imageContainer}>
          <View style={styles.container}>
            <OfflineImage
              style={styles.img}
              source={{ uri: correctImage }}
              resizeMode="contain"
            />
            {isCorrect ? <FeedBack status="correct" /> : null}
          </View>
        </View>
        {isSelf ? (
          <View style={styles.flex1ItemsCenter}>
            <View style={styles.videoWrapper}>
              <OfflineWrapper url={audioUrl}>
                <VideoPlayer
                  autoPlay
                  noVideo
                  url={audioUrl}
                  correctSelected={isCorrect}
                  onCorrect={() => next()}
                  playbackRate={playbackRate}
                />
              </OfflineWrapper>
            </View>
          </View>
        ) : (
          <View style={styles.flex2}>
            {!!peek && (
              <ResponsiveText style={styles.exerciseText}>
                {word}
              </ResponsiveText>
            )}
          </View>
        )}
      </View>
      <View style={styles.keyboardAreaSentence}>
        {isFullKeyboard ? (
          <FullKeyboard
            currentOrder={currentOrder}
            splitLetters={splitLetters}
            onCorrect={onFullKeyboardCorrect}
            onWrong={onWrong}
            word={word}
            isWordset
            capitalizeAll={isCapital}
            wrongBoxChosen={!isSelf ? 0 : wrongBoxChosen}
          />
        ) : (
          <ClickableWordSquareContainer
            currentOrder={wordOrder}
            guessedWords={guessedWords}
            onCorrect={onCorrect}
            onWrong={onWrong}
            splitWords={splitWords}
            word={word}
            wrongBoxChosen={!isSelf ? 0 : wrongBoxChosen}
          />
        )}
      </View>
    </View>
  );
}
